import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Download, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadBlob } from "@/components/hr/hrUtils";
import { BTN, DOC_TYPES, useMaint } from "./maintUtils";

export function DocsPanel({ equipmentId, docs, onChanged, showEquipment }) {
  const m = useMaint();
  const [f, setF] = useState({ doc_type: "manuel", title: "", as_photo: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async () => {
    if (!file) { toast.error("Choisissez un fichier"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("equipment_id", equipmentId); fd.append("doc_type", f.as_photo ? "photo" : f.doc_type);
    fd.append("title", f.title); fd.append("as_photo", f.as_photo ? "1" : "");
    setBusy(true);
    try { await api.post("/maint/documents", fd, { timeout: 180000 }); toast.success("Document ajouté"); setFile(null); setF({ doc_type: "manuel", title: "", as_photo: false }); if (ref.current) ref.current.value = ""; onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (d) => { if (!window.confirm(`Supprimer « ${d.title} » ?`)) return; try { await api.delete(`/maint/documents/${d.id}`); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="space-y-3" data-testid="maint-docs">
      {equipmentId && (m.can("create") || m.can("edit")) && (
        <div className="grid gap-2 rounded-2xl bg-gray-50 p-3 sm:grid-cols-[1fr_1fr_1.3fr_auto]">
          <Select value={f.doc_type} onValueChange={(v) => setF({ ...f, doc_type: v })}><SelectTrigger data-testid="maint-doc-type" className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          <Input data-testid="maint-doc-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Intitulé" className="rounded-xl bg-white" />
          <div className="flex flex-col justify-center gap-1">
            <input ref={ref} data-testid="maint-doc-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.mp4,.mov" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" />
            <label className="flex items-center gap-1.5 text-xs"><input data-testid="maint-doc-as-photo" type="checkbox" checked={f.as_photo} onChange={(e) => setF({ ...f, as_photo: e.target.checked })} className="accent-[#065F46]" />Photo principale de l'appareil</label>
          </div>
          <Button data-testid="maint-doc-upload" disabled={busy} onClick={upload} className={BTN}><Upload className="mr-1 h-4 w-4" />{busy ? "Envoi…" : "Ajouter"}</Button>
        </div>
      )}
      {docs.length === 0 ? <p className="text-sm text-gray-400">Aucun document.</p> : (
        <div className="divide-y divide-gray-50">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 py-2" data-testid={`maint-doc-${d.id}`}>
              {d.doc_type === "photo" ? <ImageIcon className="h-4 w-4 text-[#065F46]" /> : <FileText className="h-4 w-4 text-[#065F46]" />}
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{d.title}</p>
                <p className="text-xs text-gray-400">{showEquipment ? `${showEquipment(d.equipment_id)} · ` : ""}{DOC_TYPES[d.doc_type] || d.doc_type} · {Math.max(1, Math.round(d.size / 1024))} Ko · {fmtDate(d.created_at)}</p></div>
              <button data-testid={`maint-doc-dl-${d.id}`} onClick={() => downloadBlob(`/maint/documents/${d.id}/download`, d.original_filename).catch((e) => toast.error(apiErr(e)))} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"><Download className="h-4 w-4" /></button>
              {m.can("delete") && <button onClick={() => del(d)} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function QrDialog({ eq, onClose }) {
  const url = `${window.location.origin}/maintenance/equipement/${eq.id}`;
  const print = () => {
    const svg = document.getElementById("maint-qr-svg")?.outerHTML || "";
    const w = window.open("", "_blank", "width=420,height=520");
    if (!w) return;
    w.document.write(`<html><head><title>QR ${eq.name}</title></head><body style="font-family:sans-serif;text-align:center;padding:24px">${svg}<h2 style="margin:12px 0 4px;text-transform:uppercase">${eq.name.replace(/</g, "&lt;")}</h2><p style="margin:0;color:#555">${(eq.serial_number || "").replace(/</g, "&lt;")}</p><p style="font-size:11px;color:#888">Cabinet Ida Gray — scanner pour ouvrir la fiche</p></body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}><DialogContent className="rounded-3xl sm:max-w-sm" data-testid="maint-qr-dialog">
      <DialogHeader><DialogTitle className="font-display text-xl">QR code de l'appareil</DialogTitle></DialogHeader>
      <div className="flex flex-col items-center gap-2 py-2">
        <QRCodeSVG id="maint-qr-svg" value={url} size={220} level="M" includeMargin />
        <p className="font-black uppercase">{eq.name}</p>
        <p className="break-all text-center text-[11px] text-gray-400" data-testid="maint-qr-url">{url}</p>
        <Button data-testid="maint-qr-print" onClick={print} className={`w-full ${BTN}`}>Imprimer l'étiquette</Button>
      </div>
    </DialogContent></Dialog>
  );
}
